<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Button Toggle Component
      p.text--secondary A button toggle component for switching between multiple options with support for single and multiple selections.

    // Button Toggle Variations
    .component-section.mb-8#basic-variations
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-variations')") Basic Variations
      .variations-grid
        .variation-item.mb-6(v-for="variation in btnToggleVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            // Basic toggle with value display
            template(v-if="variation.id === 'basic-toggle'")
              .mb-3
                strong Selected: {{ basicToggleValue }}
              yz-btn-toggle(v-model="basicToggleValue" :items="basicItems" mandatory)

            // Multiple selection
            template(v-else-if="variation.id === 'multiple-selection'")
              .mb-3
                strong Selected: {{ JSON.stringify(multipleToggleValue) }}
              yz-btn-toggle(v-model="multipleToggleValue" :items="basicItems" multiple)

            // Block/Full width
            template(v-else-if="variation.id === 'block-toggle'")
              .mb-3
                strong Selected: {{ blockToggleValue }}
              yz-btn-toggle(v-model="blockToggleValue" :items="basicItems" mandatory block)

            // Dense mode
            template(v-else-if="variation.id === 'dense-toggle'")
              .mb-3
                strong Selected: {{ denseToggleValue }}
              yz-btn-toggle(v-model="denseToggleValue" :items="basicItems" mandatory dense)

            // Disabled state
            template(v-else-if="variation.id === 'disabled-toggle'")
              .mb-3
                strong Selected: {{ disabledToggleValue }}
              yz-btn-toggle(v-model="disabledToggleValue" :items="basicItems" mandatory disabled)

            // Custom colors
            template(v-else-if="variation.id === 'custom-colors'")
              .mb-4
                .mb-2
                  strong Selected: {{ customColorsValue1 }}
                yz-btn-toggle(v-model="customColorsValue1" :items="basicItems" mandatory radius="20px" color="white" active-color="primary-500" text-color="neutral-600" active-text-color="white" border-color="primary-200" active-border-color="primary-500" hover-color="primary-50")
              .mb-3
                .mb-2
                  strong Selected: {{ customColorsValue2 }}
                yz-btn-toggle(v-model="customColorsValue2" :items="basicItems" mandatory radius="8px" color="neutral-100" active-color="success-500" text-color="neutral-700" active-text-color="white" border-color="neutral-300" active-border-color="success-600" hover-color="success-50")

            // String items
            template(v-else-if="variation.id === 'string-items'")
              .mb-3
                strong Selected: {{ stringToggleValue }}
              yz-btn-toggle(v-model="stringToggleValue" :items="stringItems" mandatory)

            // Custom item properties
            template(v-else-if="variation.id === 'custom-item-props'")
              .mb-3
                strong Selected: {{ customPropsValue }}
              yz-btn-toggle(v-model="customPropsValue" :items="customPropsItems" mandatory item-text="label" item-value="id")

            // Item disabled state
            template(v-else-if="variation.id === 'item-disabled'")
              .mb-3
                strong Selected: {{ itemDisabledValue }}
              yz-btn-toggle(v-model="itemDisabledValue" :items="itemsWithDisabled" mandatory)

            // Without mandatory
            template(v-else-if="variation.id === 'non-mandatory'")
              .mb-3
                strong Selected: {{ nonMandatoryValue }}
              yz-btn-toggle(v-model="nonMandatoryValue" :items="basicItems")

            // Custom radius
            template(v-else-if="variation.id === 'custom-radius'")
              .mb-4
                .mb-2
                  strong Selected: {{ radiusValue1 }}
                yz-btn-toggle(v-model="radiusValue1" :items="basicItems" mandatory radius="4px")
              .mb-4
                .mb-2
                  strong Selected: {{ radiusValue2 }}
                yz-btn-toggle(v-model="radiusValue2" :items="basicItems" mandatory radius="20px")
              .mb-3
                .mb-2
                  strong Selected: {{ radiusValue3 }}
                yz-btn-toggle(v-model="radiusValue3" :items="basicItems" mandatory radius="0")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Complete Example
    .component-section.mb-8#complete-example
      h3.mb-4.cursor-pointer(@click="navigateToSection('complete-example')") Complete Example
      .mb-4
        p A complete implementation showing button toggle with dynamic content:

      .variation-item.mb-6
        .variation-preview.mb-4.pa-4.border.rounded
          .mb-3
            strong Selected View: {{ viewTypeNames[completeExampleValue] }}
          yz-btn-toggle(v-model="completeExampleValue" :items="viewTypes" mandatory block radius="10px")

          .mt-4.pa-3.border.rounded(style="background-color: #fafafa;")
            div(v-if="completeExampleValue === 0")
              .yz-semibold-body-sm.mb-2 List View Content
              p Display items in a vertical list format with detailed information for each entry.
            div(v-if="completeExampleValue === 1")
              .yz-semibold-body-sm.mb-2 Grid View Content
              p Show items in a grid layout with thumbnail images and compact information.
            div(v-if="completeExampleValue === 2")
              .yz-semibold-body-sm.mb-2 Table View Content
              p Present data in a structured table format with sortable columns and filters.

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === completeExampleCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === completeExampleCode ? 'success' : ''" @click="copyCode(completeExampleCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ completeExampleCode }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API

      .mb-6
        h4.mb-3 YzBtnToggle Props
        .props-table
          v-simple-table
            template(v-slot:default)
              thead
                tr
                  th Prop
                  th Type
                  th Default
                  th Description
              tbody
                tr(v-for="prop in btnToggleProps" :key="prop.name")
                  td.font-weight-medium.text-no-wrap {{ prop.name }}
                  td.text--secondary.text-no-wrap {{ prop.type }}
                  td.text--secondary.text-no-wrap {{ prop.default }}
                  td.single-line {{ prop.description }}

    // Events Documentation
    .component-section.mb-8#events
      h3.mb-4.cursor-pointer(@click="navigateToSection('events')") Events
      .events-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Event
                th Payload
                th Description
            tbody
              tr(v-for="event in btnToggleEvents" :key="event.name")
                td.font-weight-medium.text-no-wrap {{ event.name }}
                td.text--secondary.text-no-wrap {{ event.payload }}
                td.single-line {{ event.description }}

</template>

<script>
export default {
  data() {
    return {
      copiedCode: null,

      // Active toggle states
      basicToggleValue: 0,
      multipleToggleValue: [0],
      blockToggleValue: 0,
      denseToggleValue: 0,
      disabledToggleValue: 1,
      customColorsValue1: 0,
      customColorsValue2: 0,
      stringToggleValue: 'option1',
      customPropsValue: 'design',
      itemDisabledValue: 0,
      nonMandatoryValue: null,
      radiusValue1: 0,
      radiusValue2: 0,
      radiusValue3: 0,
      completeExampleValue: 0,

      // Items arrays
      basicItems: [
        { name: 'Design', value: 0 },
        { name: 'Content', value: 1 },
        { name: 'Marketing', value: 2 }
      ],

      stringItems: ['option1', 'option2', 'option3'],

      customPropsItems: [
        { label: 'Design', id: 'design' },
        { label: 'Content', id: 'content' },
        { label: 'Marketing', id: 'marketing' }
      ],

      itemsWithDisabled: [
        { name: 'Active', value: 0 },
        { name: 'Disabled', value: 1, disabled: true },
        { name: 'Another', value: 2 }
      ],

      viewTypes: [
        { name: 'List', value: 0 },
        { name: 'Grid', value: 1 },
        { name: 'Table', value: 2 }
      ],

      viewTypeNames: ['List', 'Grid', 'Table'],

      btnToggleVariations: [
        {
          name: 'Basic Toggle (Mandatory)',
          id: 'basic-toggle',
          description: 'Standard button toggle with mandatory selection',
          code: `yz-btn-toggle(v-model="selectedValue" :items="items" mandatory)

// Script
data() {
  return {
    selectedValue: 0,
    items: [
      { name: 'Design', value: 0 },
      { name: 'Content', value: 1 },
      { name: 'Marketing', value: 2 }
    ]
  }
}`
        },
        {
          name: 'Multiple Selection',
          id: 'multiple-selection',
          description: 'Allow selecting multiple options at once',
          code: `yz-btn-toggle(v-model="selectedValues" :items="items" multiple)

// Script
data() {
  return {
    selectedValues: [0], // Array for multiple selection
    items: [
      { name: 'Design', value: 0 },
      { name: 'Content', value: 1 },
      { name: 'Marketing', value: 2 }
    ]
  }
}`
        },
        {
          name: 'Block/Full Width',
          id: 'block-toggle',
          description: 'Full-width button toggle that stretches to container',
          code: `yz-btn-toggle(v-model="selectedValue" :items="items" mandatory block)`
        },
        {
          name: 'Dense Mode',
          id: 'dense-toggle',
          description: 'Compact button toggle with reduced padding',
          code: `yz-btn-toggle(v-model="selectedValue" :items="items" mandatory dense)`
        },
        {
          name: 'Disabled State',
          id: 'disabled-toggle',
          description: 'Disabled button toggle prevents user interaction',
          code: `yz-btn-toggle(v-model="selectedValue" :items="items" mandatory disabled)`
        },
        {
          name: 'Custom Colors',
          id: 'custom-colors',
          description: 'Fully customizable colors for inactive, active, hover, text, and borders',
          code: `// Primary themed toggle
yz-btn-toggle(
  v-model="selectedValue"
  :items="items"
  mandatory
  radius="20px"
  color="white"
  active-color="primary-500"
  text-color="neutral-600"
  active-text-color="white"
  border-color="primary-200"
  active-border-color="primary-500"
  hover-color="primary-50"
)

// Success themed toggle
yz-btn-toggle(
  v-model="selectedValue"
  :items="items"
  mandatory
  radius="8px"
  color="neutral-100"
  active-color="success-500"
  text-color="neutral-700"
  active-text-color="white"
  border-color="neutral-300"
  active-border-color="success-600"
  hover-color="success-50"
)`
        },
        {
          name: 'String Items',
          id: 'string-items',
          description: 'Simple string array as items',
          code: `yz-btn-toggle(v-model="selectedValue" :items="items" mandatory)

// Script
data() {
  return {
    selectedValue: 'option1',
    items: ['option1', 'option2', 'option3']
  }
}`
        },
        {
          name: 'Custom Item Properties',
          id: 'custom-item-props',
          description: 'Use custom property names for item text and value',
          code: `yz-btn-toggle(
  v-model="selectedValue"
  :items="items"
  mandatory
  item-text="label"
  item-value="id"
)

// Script
data() {
  return {
    selectedValue: 'design',
    items: [
      { label: 'Design', id: 'design' },
      { label: 'Content', id: 'content' },
      { label: 'Marketing', id: 'marketing' }
    ]
  }
}`
        },
        {
          name: 'Individual Item Disabled',
          id: 'item-disabled',
          description: 'Disable specific items while keeping others active',
          code: `yz-btn-toggle(v-model="selectedValue" :items="items" mandatory)

// Script
data() {
  return {
    selectedValue: 0,
    items: [
      { name: 'Active', value: 0 },
      { name: 'Disabled', value: 1, disabled: true },
      { name: 'Another', value: 2 }
    ]
  }
}`
        },
        {
          name: 'Non-Mandatory',
          id: 'non-mandatory',
          description: 'Allow deselecting all options (no mandatory selection)',
          code: `yz-btn-toggle(v-model="selectedValue" :items="items")

// Script
data() {
  return {
    selectedValue: null, // Can be null when nothing selected
    items: [
      { name: 'Design', value: 0 },
      { name: 'Content', value: 1 },
      { name: 'Marketing', value: 2 }
    ]
  }
}`
        },
        {
          name: 'Custom Border Radius',
          id: 'custom-radius',
          description: 'Customize border radius from sharp to fully rounded',
          code: `// Sharp corners
yz-btn-toggle(v-model="value1" :items="items" mandatory radius="4px")

// Rounded
yz-btn-toggle(v-model="value2" :items="items" mandatory radius="20px")

// No radius
yz-btn-toggle(v-model="value3" :items="items" mandatory radius="0")`
        }
      ],

      btnToggleProps: [
        {
          name: 'value (v-model)',
          type: 'String, Number, Array',
          default: 'null',
          description: 'Current selection. Use String/Number for single selection, Array for multiple selection'
        },
        {
          name: 'items',
          type: 'Array',
          default: '(required)',
          description: 'Array of items to display. Can be strings, numbers, or objects with name/label property'
        },
        {
          name: 'mandatory',
          type: 'Boolean',
          default: 'false',
          description: 'Require at least one selection. Prevents deselecting the last selected item'
        },
        {
          name: 'multiple',
          type: 'Boolean',
          default: 'false',
          description: 'Allow multiple selections. Changes v-model to Array type'
        },
        {
          name: 'radius',
          type: 'String, Number',
          default: "'10px'",
          description: 'Border radius of the toggle group'
        },
        {
          name: 'color',
          type: 'String',
          default: "'white'",
          description: 'Background color for inactive buttons. Supports YDS color tokens (e.g. "neutral-100"), hex colors, or CSS variables'
        },
        {
          name: 'active-color',
          type: 'String',
          default: "'neutral-300'",
          description: 'Background color for active/selected button. Supports YDS color tokens, hex colors, or CSS variables'
        },
        {
          name: 'text-color',
          type: 'String',
          default: "'neutral-500'",
          description: 'Text color for inactive buttons. Supports YDS color tokens, hex colors, or CSS variables'
        },
        {
          name: 'active-text-color',
          type: 'String',
          default: "'neutral-700'",
          description: 'Text color for active/selected button. Supports YDS color tokens, hex colors, or CSS variables'
        },
        {
          name: 'border-color',
          type: 'String',
          default: "'neutral-400'",
          description: 'Border color for buttons. Supports YDS color tokens, hex colors, or CSS variables'
        },
        {
          name: 'active-border-color',
          type: 'String',
          default: "'neutral-700'",
          description: 'Border color for active/selected button. Supports YDS color tokens, hex colors, or CSS variables'
        },
        {
          name: 'hover-color',
          type: 'String',
          default: "'neutral-200'",
          description: 'Background color on hover for inactive buttons. Supports YDS color tokens, hex colors, or CSS variables'
        },
        {
          name: 'disabled',
          type: 'Boolean',
          default: 'false',
          description: 'Disable the entire toggle group'
        },
        {
          name: 'dense',
          type: 'Boolean',
          default: 'false',
          description: 'Compact/dense size with reduced padding (4px 12px instead of 8px 16px)'
        },
        {
          name: 'block',
          type: 'Boolean',
          default: 'false',
          description: 'Make toggle full-width to fill container'
        },
        {
          name: 'item-text',
          type: 'String',
          default: "'name'",
          description: 'Property name to use for item labels when items are objects'
        },
        {
          name: 'item-value',
          type: 'String',
          default: "'value'",
          description: 'Property name to use for item values when items are objects'
        }
      ],

      btnToggleEvents: [
        {
          name: 'input',
          payload: 'String|Number|Array',
          description: 'Emitted when selection changes (v-model support). Payload is single value or array depending on multiple prop'
        },
        {
          name: 'change',
          payload: 'String|Number|Array',
          description: 'Emitted when selection changes. Same as input event'
        }
      ]
    }
  },

  computed: {
    completeExampleCode() {
      return `template(lang="pug")
  div
    .mb-3
      strong Selected View: {{ viewTypeNames[selectedView] }}
    yz-btn-toggle(
      v-model="selectedView"
      :items="viewTypes"
      mandatory
      block
      radius="10px"
    )

    .mt-4.pa-3.border.rounded
      div(v-if="selectedView === 0")
        h4 List View Content
        p Display items in a vertical list format...
      div(v-if="selectedView === 1")
        h4 Grid View Content
        p Show items in a grid layout...
      div(v-if="selectedView === 2")
        h4 Table View Content
        p Present data in a structured table format...

script
  export default {
    data() {
      return {
        selectedView: 0,
        viewTypes: [
          { name: 'List', value: 0 },
          { name: 'Grid', value: 1 },
          { name: 'Table', value: 2 }
        ],
        viewTypeNames: ['List', 'Grid', 'Table']
      }
    }
  }`
    }
  },

  mounted() {
    this.scrollToSection()
  },

  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },

  methods: {
    copyCode(code) {
      navigator.clipboard.writeText(code).then(() => {
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      })
    },

    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },

    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table, .events-table {
  overflow-x: auto;
}

.props-table .v-data-table,
.events-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}

.gap-2 {
  gap: 8px;
}

.gap-2 > * {
  margin: 0;
}
</style>
